import { defineStore } from "pinia";
import { type ArtworkState, editArtwork, creatArtwork, cancelGenerate } from "@/views/workspace/service";
import { cloneDeep, isEqual, set } from "lodash";
import { $bus } from "@/mitt";
import { ModuleEnum, resolveStoreModuleId } from "@/assets/artwork";
import type { Channel } from "@/types/artwork";
import type { Module, ModuleRoute, GenerateParams, GenerateResult, ICreativeSketchParams } from "@/types/artwork/generate";
import { useModelStore } from "@/store/model";
import { useUserStore } from "@/store/user";
import { initParams } from "@/assets/artwork";
import { redrawWeightMin, redrawWeightMax, redrawWeightDefault, startGenerate } from "@/assets/artwork";
import { message } from "ant-design-vue";
import { useGeneratingVersions } from "@/hooks/useGeneratingVersions";
import { initGenerateResult } from "@/hooks/useGenerate";
import { hasMemberPackageParam } from "@/assets/artwork/vipPackageHandle";
import { useSmartPromptFinishModule } from "@/views/workspace/hooks/useSmartPromptFinishModule";
import { useNewUserGuide } from "@/store/newUserGuide";
import { sketchModelId } from "@/assets/artwork/module/sketch";
import { expansionModelId } from "@/assets/artwork/module/expansion";
import { getUserModelId } from "@/views/workspace/hooks/common";
import { updateModelInfo } from "./utils";
import { getArtworkName } from "@/views/workspace/hooks/index";
import { ImageLabelItem, ModuleId } from "@xmiles/holopix-types";
import { RenderAspectRatios } from "@/assets/artwork/options";

const initArtworkState: Readonly<ArtworkState> = {
  id: 0,
  artworkName: "",
  modelList: []
};

// 定义在store中实际存在的模块类型
type StoreModuleRoute = Exclude<ModuleRoute, "palette" | "downloadVideo">;

// 辅助函数：检查模块是否在store中存在
function isStoreModule(moduleRoute: ModuleRoute): moduleRoute is StoreModuleRoute {
  return moduleRoute !== "palette" && moduleRoute !== "downloadVideo";
}

export const useArtworkStore = defineStore("artwork", {
  state: () => ({
    initArtworkName: "", // 进入创作是，系统给定的初始作品名称
    artworkState: cloneDeep(initArtworkState) as ArtworkState,
    hasStateUpdate: false, // 非编辑入口进入时，用于标识是否需要显示挽留提示
    channel: "normal" as Channel,
    explore: {
      isGenerate: false,
      params: cloneDeep(initParams.explore) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {} as Partial<GenerateParams>,
      _isFromReuse: false,
      creativeSketchParams: {
        originImgSrc: ""
      } as ICreativeSketchParams
    },
    colour: {
      isGenerate: false,
      params: cloneDeep(initParams.colour) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {} as Partial<GenerateParams>,
      creativeSketchParams: {
        originImgSrc: ""
      } as ICreativeSketchParams
    },
    refine: {
      isGenerate: false,
      params: cloneDeep(initParams.refine) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {} as Partial<GenerateParams>,
      creativeSketchParams: {
        originImgSrc: ""
      } as ICreativeSketchParams
    },
    sketch: {
      isGenerate: false,
      params: cloneDeep(initParams.sketch) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {} as Partial<GenerateParams>,
      creativeSketchParams: {
        originImgSrc: ""
      } as ICreativeSketchParams
    },
    fission: {
      isGenerate: false,
      params: cloneDeep(initParams.fission) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {} as Partial<GenerateParams>,
      creativeSketchParams: {
        originImgSrc: ""
      } as ICreativeSketchParams
    },
    generateThreeTransform: {
      isGenerate: false,
      params: {},
      result: {} as any,
      lastVersionParams: {}
    },
    threeTransform: {
      isGenerate: false,
      params: cloneDeep(initParams.threeTransform) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    matting: {
      isGenerate: false,
      params: cloneDeep(initParams.matting) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    replace: {
      isGenerate: false,
      params: cloneDeep(initParams.replace) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {},
      targetModuleRef: null as any // 当前模块内容组件 Ref
    },
    expansion: {
      isGenerate: false,
      params: cloneDeep(initParams.expansion) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {},
      targetModuleRef: null as any // 当前模块内容组件 Ref
    },
    zoom: {
      isGenerate: false,
      params: cloneDeep(initParams.zoom) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    pixelate: {
      isGenerate: false,
      params: cloneDeep(initParams.pixelate) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    aivideo: {
      isGenerate: false,
      params: cloneDeep(initParams.aivideo) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    expression: {
      isGenerate: false,
      params: cloneDeep(initParams.expression) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    render: {
      isGenerate: false,
      params: cloneDeep(initParams.render) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    freePerspective: {
      isGenerate: false,
      params: cloneDeep(initParams.freePerspective) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    styleRenderTrain: {
      isGenerate: false,
      params: cloneDeep(initParams.styleRenderTrain) as any,
      result: cloneDeep(initGenerateResult) as Partial<GenerateResult>,
      lastVersionParams: {}
    },
    skipResetParamsModules: [] as number[], // 不需要更新数据的创作模块
    skipResetResultModules: [] as number[], // 不需要更新结果的创作模块
    labels: [] as ImageLabelItem[],
    sidebarCollapse: false
  }),
  getters: {
    firstModel({ artworkState }) {
      return artworkState.modelList[0];
    },
    isFlux(): boolean {
      return this.firstModel?.ckptType === "Flux";
    },
    generating(): boolean {
      return this.explore.isGenerate || this.colour.isGenerate || this.refine.isGenerate;
    }
  },
  actions: {
    async updateArtwork(newArtworkInfo: Partial<ArtworkState>, { edit = false, updateTag = true, updateModelState = true } = {}) {
      if (newArtworkInfo.modelList?.length === 0) {
        useModelStore().resetModalState();
        useModelStore().open = true;
      }

      // 判断更新的模型是否为线稿提取专用模型
      if (newArtworkInfo.modelList && newArtworkInfo.modelList.some((item) => item.id === sketchModelId)) {
        // 设置为默认模型
        const modelId = await getUserModelId();
        newArtworkInfo.modelList = [{ id: modelId } as any];
      }

      // 判断更新的模型是否为智能扩图专用模型
      if (newArtworkInfo.modelList && newArtworkInfo.modelList.some((item) => item.id === expansionModelId)) {
        // 设置为默认模型
        const modelId = await getUserModelId();
        newArtworkInfo.modelList = [{ id: modelId } as any];
      }

      // 更新模型信息
      if (newArtworkInfo.modelList && updateModelState) {
        await updateModelInfo(newArtworkInfo.modelList);
        edit = true;
      }

      // 修复模型强度默认值为空
      this.artworkState.modelList.forEach((item) => {
        if (typeof item.maxStrength === "number" && typeof item.minStrength === "number" && typeof item.strength !== "number") {
          item.strength = (item.maxStrength - item.minStrength) / 2 + item.minStrength;
        }
      });
      // 如果作品名称为空，则设置一个默认值
      if (!this.artworkState.artworkName && !newArtworkInfo.artworkName) {
        this.artworkState.artworkName = getArtworkName();
      }

      if (this.artworkState.id) {
        this.hasStateUpdate = false;

        if (edit && !isEqual(this.artworkState, { ...this.artworkState, ...newArtworkInfo })) {
          const modelList = newArtworkInfo.modelList || this.artworkState.modelList;

          await editArtwork({
            ...this.artworkState,
            ...newArtworkInfo,
            artworkName: newArtworkInfo.artworkName || this.artworkState.artworkName,
            modelList: JSON.stringify(modelList)
          });
        }
      } else {
        // 未创建的作品修改作品名称、风格时
        if (
          (newArtworkInfo.artworkName && newArtworkInfo.artworkName !== this.initArtworkName) ||
          (newArtworkInfo.modelList && newArtworkInfo.modelList.length > 0 && this.artworkState.modelList.length > 0)
        ) {
          this.hasStateUpdate = true;
        }
      }

      // 更新第一个模型时，更新显性提示词
      if (newArtworkInfo.modelList && !isEqual(newArtworkInfo.modelList[0], this.artworkState.modelList[0]) && updateTag) {
        if (newArtworkInfo.modelList[0]) {
          // 通用、场景分类的模型，关闭简单画面背景开关
          if ([1, 4].includes(newArtworkInfo.modelList[0].category)) {
            this.explore.params.simpleBackground = false;
            this.threeTransform.params.simpleBackground = false;
          } else {
            this.explore.params.simpleBackground = true;
            this.threeTransform.params.simpleBackground = true;
          }
        }
      }

      // 使用 HOLO - V1 模型时，关闭简单画面背景
      if (newArtworkInfo.modelList && newArtworkInfo.modelList[0]?.ckptType === "Flux") {
        for (let key in ModuleEnum) {
          if (Number(key)) {
            // 跳过palette和generateThreeTransform模块，因为它们不在store中管理状态或没有params
            if (Number(key) === ModuleEnum.palette || Number(key) === ModuleEnum.generateThreeTransform) {
              continue;
            }

            const targetModule = ModuleEnum[Number(key)] as ModuleRoute;

            if (isStoreModule(targetModule) && (this as any)[targetModule]?.params?.simpleBackground) {
              (this as any)[targetModule].params.simpleBackground = false;
            }
          }
        }
      }

      this.artworkState = { ...this.artworkState, ...newArtworkInfo };
    },
    resetArtwork() {
      this.artworkState = cloneDeep(initArtworkState);
      this.initArtworkName = "";
      this.hasStateUpdate = false;
      this.resetArtworkModule(true); // 强制重置，跳过保护逻辑

      const { clear } = useGeneratingVersions();
      clear();

      // 清除智能提示词状态
      const { clear: clearSmartPromptFinishModule } = useSmartPromptFinishModule();
      clearSmartPromptFinishModule();
    },
    resetArtworkModule(forceReset = false) {
      for (let key in ModuleEnum) {
        if (Number(key)) {
          // 跳过palette模块，因为它不在store中管理状态
          if (Number(key) === ModuleEnum.palette) {
            continue;
          }

          if (!this.skipResetParamsModules.includes(Number(key))) {
            this.resetModuleParams(Number(key) as Module, undefined, forceReset);
            // console.log('resetModuleParams', Number(key) as Module)
          }

          if (!this.skipResetResultModules.includes(Number(key))) {
            const moduleRoute = ModuleEnum[key] as ModuleRoute;
            if (isStoreModule(moduleRoute) && (this as any)[moduleRoute]) {
              this.resetModuleResult(Number(key) as Module);
              (this as any)[moduleRoute].isGenerate = false;
            }
          }

          this.resetModuleLastVersionParams(Number(key) as Module);
        }
      }
    },

    resetModuleParams(module: Module, params?: any, forceReset = false) {
      // 跳过palette模块，因为它不在store中管理状态
      if (module === ModuleEnum.palette) {
        return;
      }
      const storeModule = resolveStoreModuleId(module as number);
      const targetModule = ModuleEnum[storeModule] as ModuleRoute;

      if (isStoreModule(targetModule) && (this as any)[targetModule]) {
        // 丰富细节、局部细化 重绘幅度旧数据处理
        if ([3, 5].includes(storeModule) && params && params.redrawWeight) {
          // 旧数据重绘幅度重置为默认值
          if (params.redrawWeight > redrawWeightMax || params.redrawWeight < redrawWeightMin) {
            params.redrawWeight = redrawWeightDefault;
          }
        }

        // 一键成稿 使用了形象样式参考修复权重被清除
        if (storeModule === 1 && params?.styleReferList && params.styleReferList.length > 0) {
          const styleReferIdList = (params as any).styleReferIdList;

          if (Array.isArray(styleReferIdList)) {
            params.styleReferList.forEach((item: any, index: number) => {
              if (styleReferIdList[index] && styleReferIdList[index].weight !== undefined) {
                item.weight = styleReferIdList[index].weight * 100;
              }
            });
          }
        }

        // 移出参数中的 ctime
        if (params && params.ctime) {
          delete params.ctime;
        }

        // 一键成稿：非复用场景下，如果传入的 styleReferList 为空数组，则保留当前草稿中的参考图设置。
        // 编辑复用必须以历史参数为准；否则同一作品内切换不同版本时，会把上一张图的参考图串到当前版本。
        if (module === ModuleEnum.explore && params && Array.isArray(params.styleReferList) && params.styleReferList.length === 0) {
          const currentStyleReferList = (this as any)[targetModule].params?.styleReferList;
          const isReuseScenario = Boolean((params as any)._isFromReuse);
          if (!isReuseScenario && currentStyleReferList && currentStyleReferList.length > 0) {
            delete params.styleReferList;
            if (process.env.NODE_ENV === "development") {
              console.log(`🛡️ [resetModuleParams] 保留人脸参考设置: styleReferList`, currentStyleReferList);
            }
          }
        }

        // 擦除模式下，负向提示词 = 正向提示词的值
        if ([ModuleEnum.replace].includes(storeModule) && params?.smearType == 2) {
          params.prompt = params.negativePrompt;
        }

        // 像素图转换，如果没有palette，则设置为空，用于解决编辑复用时使用的是原图，而色板设置已经选了自定义色板，导致复用时色板不被清空的异常
        if (params && storeModule === ModuleEnum.pixelate && !params?.palette) {
          params.palette = [];
        }

        /**
         * AI视频模块兼容火山云参数
         * 仅旧模块 25（Aivideo）需要旧参数映射，新 Seedance 模块 75-78 不需要
         */

        if (storeModule == ModuleEnum.aivideo && Number(module) === ModuleId.Aivideo) {
          // AI视频模块兼容火山云参数
          if (params) {
            // 需要删除的旧参数（不映射到新参数）
            const paramsToDelete: string[] = ["frameRate", "generateSecond", "size"];

            // 检查是否有旧版本参数需要提示
            const hasOldParams = paramsToDelete.some((param) => params?.hasOwnProperty(param));

            if (hasOldParams) {
              message.warning("目前视频生成已经升级，旧版本不可使用,已帮您切换至新版本，请调整参数后生成。");
            }

            // 处理旧版本参数映射
            const paramMappings: Record<string, string> = {
              prompt: "videoText",
              promptImg: "videoImgUrl"
            };

            // 检查并转换旧参数
            const convertedParams: any = {};
            for (const [oldKey, newKey] of Object.entries(paramMappings)) {
              if (params?.hasOwnProperty(oldKey) && params[oldKey] !== undefined) {
                convertedParams[newKey] = params[oldKey];
                delete params[oldKey];
              }
            }

            // 删除不需要映射的旧参数
            for (const paramToDelete of paramsToDelete) {
              if (params?.hasOwnProperty(paramToDelete)) {
                delete params[paramToDelete];
              }
            }

            // 合并转换后的参数
            Object.assign(params, convertedParams);
          }
        }

        // 自由视角尺寸修复
        if (storeModule === ModuleId.FreePerspective && params) {
          if (!params.aspectRatios) {
            params.aspectRatios = RenderAspectRatios.Fast;
          } else {
            params.aspectRatios = String(params.aspectRatios);
          }
        }

        if (params) {
          // 有参数传入时，合并参数而不是完全重置
          (this as any)[targetModule].params = { ...(this as any)[targetModule].params, ...params };
        } else {
          const defaultParams = cloneDeep(initParams[targetModule as keyof typeof initParams]);

          // 🛡️ 保护逻辑：只在非强制重置模式下保留重要的用户设置
          if (!forceReset) {
            const currentParams = (this as any)[targetModule].params;

            // 保留这些重要的用户设置，避免被意外清空
            let preservedFields = ["layoutReferPose", "layoutRefer", "styleReferList"];

            // 万能渲染模块在重置时需要清空 layoutRefer
            if (storeModule === ModuleEnum.render) {
              preservedFields = preservedFields.filter(field => field !== "layoutRefer");
            }

            for (const field of preservedFields) {
              if (currentParams?.[field] && currentParams[field] !== null) {
                if (defaultParams) {
                  (defaultParams as any)[field] = currentParams[field];
                }

                if (process.env.NODE_ENV === "development") {
                  console.log(`🛡️ [resetModuleParams] 保留用户设置: ${field}`, currentParams[field]);
                }
              }
            }
          } else {
            if (process.env.NODE_ENV === "development") {
              console.log(`🔥 [resetModuleParams] 强制重置模式，跳过保护逻辑`, { module: targetModule });
            }
          }

          (this as any)[targetModule].params = defaultParams;
        }

        // 添加调试日志
        if (process.env.NODE_ENV === "development") {
          const newParams = (this as any)[targetModule].params;
          console.log(`✅ [resetModuleParams] 重置完成`, {
            module: targetModule,
            forceReset,
            newLayoutReferPose: newParams?.layoutReferPose,
            allNewParams: newParams
          });
        }
      }
    },

    /**
     * 填充未发布模型参数
     */
    fillUnpublishedModelParams(module: Module, params?: any) {
      // 跳过palette模块，因为它不在store中管理状态
      if (module === ModuleEnum.palette) {
        return;
      }

      const storeModule = resolveStoreModuleId(module as number);
      const targetModule = ModuleEnum[storeModule] as ModuleRoute;

      // 擦除模式下，负向提示词 = 正向提示词的值
      if ([ModuleEnum.replace].includes(storeModule) && params?.smearType == 2) {
        params.prompt = params.negativePrompt;
      }

      const defaultParams = cloneDeep(initParams[targetModule as keyof typeof initParams]);

      set(defaultParams, "prompt", params.prompt);
      set(this, `${targetModule}.params`, defaultParams);
    },

    resetModuleResult(module: Module, result = {}) {
      const storeModule = resolveStoreModuleId(module as number);
      const targetModule = ModuleEnum[storeModule] as ModuleRoute;
      if (isStoreModule(targetModule) && (this as any)[targetModule]) {
        this[targetModule].result = result;
      }
    },
    resetModuleLastVersionParams(module: Module, params?: any) {
      const storeModule = resolveStoreModuleId(module as number);
      const targetModule = ModuleEnum[storeModule] as ModuleRoute;

      if (isStoreModule(targetModule) && (this as any)[targetModule]) {
        this[targetModule].isGenerate = false;
        this[targetModule].lastVersionParams = (
          params
            ? { ...cloneDeep(initParams[targetModule as "explore" | "colour" | "refine"]), ...params }
            : cloneDeep(initParams[targetModule as "explore" | "colour" | "refine"])
        ) as any;
      }
    },
    resetModuleGenerate(module: Module) {
      const storeModule = resolveStoreModuleId(module as number);
      const targetModule = ModuleEnum[storeModule] as ModuleRoute;
      if (isStoreModule(targetModule) && (this as any)[targetModule]) {
        this[targetModule].isGenerate = false;
      }
    },

    /**
     * 设置超清放大模式
     * @description 切换重绘放大/快速放大模式，参数独立保留
     * @see data-model.md 第 3 节 - 状态管理
     */
    setZoomMode(mode: 'redraw' | 'fast') {
      // 参数已独立存储在 fast 和 redraw 对象中，切换模式时无需额外处理
      this.zoom.params.mode = mode;
    },

    async startGenerate(
      module: Module,
      options?: {
        onClientIdGenerated?: (clientId: string) => void;
        onClientIdCleared?: () => void;
      }
    ) {
      // 是否新手引导中
      const newUserGuideStore = useNewUserGuide();
      if (newUserGuideStore.isMockGenerate && this.channel === "expedite") {
        this.channel = "normal"; // 新手引导生图使用了加速通道、改为标准通道
      }

      // 非会员用户使用了会员参数
      const showUseMemberParamsModal = await hasMemberPackageParam(module);
      if (showUseMemberParamsModal) {
        $bus.emit("useMemberParamsModal", module);
        return false;
      }

      const storeModule = resolveStoreModuleId(module as number);
      const targetModule = ModuleEnum[storeModule] as ModuleRoute;
      if (isStoreModule(targetModule) && (this as any)[targetModule]) {
        this[targetModule].isGenerate = true;
      }

      const artworkStateId = this.artworkState.id;
      if (this.artworkState.id) {
        editArtwork({ ...(this.artworkState as any), lastModule: module, modelList: JSON.stringify(this.artworkState.modelList) }); // 更新最后生图的模块
      } else {
        // 先创建作品
        if (!this.artworkState.artworkName) {
          this.artworkState.artworkName = getArtworkName();
        }
        const { data } = await creatArtwork({
          artworkName: this.artworkState.artworkName,
          modelList: JSON.stringify(this.artworkState.modelList),
          lastModule: module
        });
        this.hasStateUpdate = false;
        this.artworkState.id = data;
      }

      // 3D模型会在点击生成模型时，先创建作品，然后点击生图时，再使用当前模块生图
      if (storeModule === ModuleEnum.threeTransform && !artworkStateId) {
        return false;
      }

      // 使用 HOLO - V1 模型时，关闭简单画面背景
      if (this.isFlux) {
        const targetModule = ModuleEnum[storeModule] as ModuleRoute;

        if (isStoreModule(targetModule) && (this as any)[targetModule]?.params?.simpleBackground) {
          (this as any)[targetModule].params.simpleBackground = false;
        }
      }

      // 开启当前模块生图
      startGenerate(module as ModuleId, undefined, options);
    },

    setModuleLatest(module: Module) {
      const { getModuleLatest } = useGeneratingVersions();

      const storeModule = resolveStoreModuleId(module as number);
      const moduleLatest = getModuleLatest(storeModule as Module);
      if (moduleLatest) {
        const targetModule = ModuleEnum[storeModule] as ModuleRoute;
        if (isStoreModule(targetModule) && (this as any)[targetModule]) {
          this[targetModule].isGenerate = true;
          this[targetModule].lastVersionParams = moduleLatest.params;
          this[targetModule].result = moduleLatest.result;
        }
      } else {
        const targetModule = ModuleEnum[storeModule] as ModuleRoute;
        if (isStoreModule(targetModule) && (this as any)[targetModule]) {
          this[targetModule].isGenerate = false;
          this[targetModule].lastVersionParams = {};
          this[targetModule].result = cloneDeep(initGenerateResult);
        }
      }
    },

    async cancelGenerate({ module, clientId }: { module: Module; clientId: string }) {
      try {
        await cancelGenerate({
          module,
          clientId
        });

        message.success("取消成功");

        const { has, del } = useGeneratingVersions();

        if (has(clientId)) {
          del(clientId);
        }

        this.setModuleLatest(module);
      } catch (error) {
        message.error("取消失败");
      }
    },
    getLabltxt(id: number) {
      return this.labels.find((label) => label.id == id)?.label;
    }
  },
  persist: {
    key: "pinioArtworkStore",
    storage: window.localStorage,
    paths: ["artworkState", "generateThreeTransform"]
  }
});
